/* eslint-disable no-undef */
import axiosInstance from "./axiosInstance";
import { logEvent } from "firebase/analytics";
import { analytics } from "../utils/firebase";

/**
 * PaperLens API Service Layer
 * Refactored to match modular backend routing.
 */

// --- Research & User ---
export const signUpUser = async (uid, email) => {
  try {
    const response = await axiosInstance.post("/research/user/create", { uid, email });
    return { message: "User data synced!", status: response.data.status };
  } catch (error) {
    console.error("Error adding user data:", error);
    return { message: "Failed to add user data.", status: false };
  }
};

export const createResearch = async (uid, title, agentType = "default") => {
  try {
    const response = await axiosInstance.post("/research/create", { title, agent_type: agentType }, { params: { uid } });
    return { message: "Research created!", status: true, data: response.data.data };
  } catch (error) {
    console.error("Error creating research:", error);
    return { message: "Failed to create research.", status: false };
  }
};

export const deleteResearch = async (uid, researchId) => {
  try {
    const response = await axiosInstance.delete("/research/delete", { params: { uid, research_id: researchId } });
    return { message: "Research deleted!", status: true };
  } catch (error) {
    console.error("Error deleting research:", error);
    return { message: "Failed to delete research.", status: false };
  }
};

export const getResearches = async (uid) => {
  try {
    const response = await axiosInstance.get("/research/list", { params: { uid } });
    return { message: "Researches fetched!", status: true, data: response.data.data };
  } catch (error) {
    console.error("Error fetching researches:", error);
    return { message: "Failed to fetch researches.", status: false };
  }
};

// --- Quota ---
export const setQuota = async (uid) => {
  try {
    const response = await axiosInstance.post("/quota/set", {}, { params: { uid } });
    return { message: "Quota initialized!", status: true };
  } catch (error) {
    console.error("Error setting quota:", error);
    return { message: "Failed to initialize quota.", status: false };
  }
};

export const checkQuota = async (uid, quotaType) => {
  try {
    const response = await axiosInstance.get("/quota/check", { params: { uid, quota_type: quotaType } });
    return { message: "Quota fetched!", status: true, remaining: response.data.remaining };
  } catch (error) {
    console.error("Error checking quota:", error);
    return { message: "Failed to check quota.", status: false };
  }
};

// --- Analysis & Comparison ---
export const analyzeDocument = async (uid, researchId, file, url = null, agentId = null) => {
  try {
    const formData = new FormData();
    if (file) formData.append("file", file);
    if (url) formData.append("url", url);
    if (agentId) formData.append("agent_id", agentId);

    const response = await axiosInstance.post("/analysis/analyze", formData, {
      params: { uid, research_id: researchId },
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000
    });
    return { message: "Analysis complete!", status: response.data.status, data: response.data.data };
  } catch (error) {
    console.error("Error analyzing document:", error);
    return { message: "Analysis failed.", status: false };
  }
};

export const compareDocuments = async (uid, researchId, files, urls = [], agentId = null) => {
  try {
    const formData = new FormData();
    if (files && files.length > 0) files.forEach(file => formData.append("files", file));
    if (urls && urls.length > 0) urls.forEach(url => formData.append("urls", url));
    if (agentId) formData.append("agent_id", agentId);

    const response = await axiosInstance.post("/analysis/compare", formData, {
      params: { uid, research_id: researchId },
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 180000
    });
    return { message: "Comparison complete!", status: response.data.status, data: response.data.data };
  } catch (error) {
    console.error("Error comparing documents:", error);
    return { message: "Comparison failed.", status: false };
  }
};

export const deleteResearchItem = async (uid, researchId, itemId) => {
  try {
    const response = await axiosInstance.delete("/analysis/item/delete", { params: { uid, research_id: researchId, item_id: itemId } });
    return { message: "Item deleted!", status: true };
  } catch (error) {
    console.error("Error deleting item:", error);
    return { message: "Failed to delete item.", status: false };
  }
};

// --- Chat ---
export const createChatSession = async (uid, researchId, itemId, title) => {
  try {
    const response = await axiosInstance.post("/chat/session/create", { title }, { params: { uid, research_id: researchId, item_id: itemId } });
    return { message: "Session created!", status: true, data: response.data.data };
  } catch (error) {
    console.error("Error creating chat session:", error);
    return { message: "Failed to create chat session.", status: false };
  }
};

export const getChatSessions = async (uid, researchId, itemId) => {
  try {
    const response = await axiosInstance.get("/chat/sessions", { params: { uid, research_id: researchId, item_id: itemId } });
    return { message: "Sessions fetched!", status: true, data: response.data.data };
  } catch (error) {
    console.error("Error fetching sessions:", error);
    return { message: "Failed to fetch sessions.", status: false };
  }
};

export const deleteChatSession = async (uid, researchId, itemId, sessionId) => {
  try {
    const response = await axiosInstance.delete("/chat/session/delete", { params: { uid, research_id: researchId, item_id: itemId, session_id: sessionId } });
    return { message: "Session deleted!", status: true };
  } catch (error) {
    console.error("Error deleting session:", error);
    return { message: "Failed to delete session.", status: false };
  }
};

export const sendChatMessage = async (uid, researchId, itemId, message, sessionId = null, agentId = null, isInit = false, contextItemId = null) => {
  try {
    const response = await axiosInstance.post("/chat/send", { message, agent_id: agentId, is_init: isInit, context_item_id: contextItemId }, { params: { uid, research_id: researchId, item_id: itemId, session_id: sessionId } });
    return { message: "Message sent!", status: true, response: response.data.response };
  } catch (error) {
    console.error("Error sending chat message:", error);
    return { message: "Failed to send message.", status: false };
  }
};

// --- Admin ---
export const getAdminStats = async (uid) => {
  try {
    const response = await axiosInstance.get("/admin/stats", { params: { uid } });
    return { status: true, data: response.data };
  } catch (error) {
    console.error("Error fetching admin stats:", error);
    return { status: false, message: "Failed to fetch stats" };
  }
};

// --- Events ---
export const trackEvent = async (uid, eventName, metadata = {}) => {
  try {
    // Firebase Analytics Event
    if (analytics) {
      logEvent(analytics, eventName, { ...metadata, user_id: uid });
    }
  } catch (error) {
    console.error("Error logging event to Firebase Analytics:", error);
  }
};